import { useCallback, useRef, useState } from 'react'
import { MfaApiError, needsStepUp, otpSendWait } from '../../api/mfa'
import { mfaMessage } from './mfaCopy'

type Options<T> = {
  /** The gated request. `token` is a single-use `X-Step-Up-Token`, present on the one retry only. */
  call: (token?: string) => Promise<T>
  /** Runs with the result once the call has gone through, possibly after a re-auth or a step-up. */
  onDone: (result: T) => void
  /** Wording for a network or 5xx failure; defaults to the server's own. */
  unavailable?: string
  /** Wording for anything else; defaults to a generic try-again. */
  fallback?: string
  /** The send was refused for volume (`otp_send_limited`): no email went out; wait this many seconds. */
  onLimited?: (retryAfterSeconds: number) => void
}

/**
 * The sequence every gated backend-MFA change follows, in one place:
 * call → `reauth_required` opens the Firebase re-auth and repeats →
 * `step_up_*` opens the backend step-up and repeats ONCE with its token.
 *
 * - A step-up token is spent by that one retry and never stored; a token the
 *   backend refuses is not retried again — the error is shown.
 * - `reauth_required` always re-opens the re-auth, with or without a token.
 * - A failure after re-auth is thrown into the dialog (it stays open and shows
 *   the reason) as well as kept in `error`.
 *
 * - Cancelling the re-auth or the step-up is a terminal outcome of its own:
 *   `cancelled` is true until the next `run`, so the caller never sits on a
 *   "working" screen that nothing is working on.
 *
 * Returns the state to render and the props for `<ReauthDialog>` and
 * `<StepUpSheet>`; the caller supplies only their wording.
 */
export function useGatedMfaCall<T>({ call, onDone, unavailable, fallback, onLimited }: Options<T>) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reauth, setReauth] = useState(false)
  const [stepUp, setStepUp] = useState(false)
  const [cancelled, setCancelled] = useState(false)

  // The caller's closures change every render; the latest is what must run.
  const latest = useRef({ call, onDone, unavailable, fallback, onLimited })
  latest.current = { call, onDone, unavailable, fallback, onLimited }

  type Outcome = 'done' | 'reauth' | 'stepup' | { failed: string }
  const attempt = useCallback(async (token?: string): Promise<Outcome> => {
    try {
      latest.current.onDone(await latest.current.call(token))
      return 'done'
    } catch (thrown) {
      if (thrown instanceof MfaApiError && thrown.kind === 'reauth_required') return 'reauth'
      if (needsStepUp(thrown) && token === undefined) return 'stepup'
      const wait = otpSendWait(thrown)
      if (wait !== null) latest.current.onLimited?.(wait)
      return { failed: mfaMessage(thrown, {
          ...(latest.current.unavailable !== undefined && { unavailable: latest.current.unavailable }),
          ...(latest.current.fallback !== undefined && { fallback: latest.current.fallback }),
        }) }
    }
  }, [])

  const run = useCallback(
    async (token?: string) => {
      setBusy(true)
      setError(null)
      setCancelled(false)
      const outcome = await attempt(token)
      setBusy(false)
      if (outcome === 'reauth') setReauth(true)
      else if (outcome === 'stepup') setStepUp(true)
      else if (typeof outcome === 'object') setError(outcome.failed)
    },
    [attempt],
  )

  const start = useCallback(() => run(), [run])

  return {
    run: start,
    busy,
    error,
    /** The user declined the re-auth or the step-up. Cleared by the next `run`. */
    cancelled,
    /** A re-auth or step-up is being asked for; the call has not gone through yet. */
    waiting: reauth || stepUp,
    reauthProps: {
      open: reauth,
      onCancel: () => {
        setReauth(false)
        setCancelled(true)
      },
      onReauthed: async () => {
        // Throws on failure so the dialog shows it and stays open.
        setBusy(true)
        const outcome = await attempt()
        setBusy(false)
        if (outcome === 'reauth') throw new Error('We still couldn’t confirm it’s you. Try again.')
        setReauth(false)
        if (outcome === 'stepup') setStepUp(true)
        else if (typeof outcome === 'object') {
          setError(outcome.failed)
          throw new Error(outcome.failed)
        }
      },
    },
    stepUpProps: {
      open: stepUp,
      onCancel: () => {
        setStepUp(false)
        setCancelled(true)
      },
      onVerified: (token: string) => {
        setStepUp(false)
        void run(token)
      },
    },
  }
}
